/* ============================================================
   product.js — product page interactions.
     - Gallery thumb click / keyboard nav (swaps main image)
     - VAT inc/ex toggle
     - Quantity stepper
     - Delivery countdown (14:00 Mon–Fri cutoff)
     - Sticky sub-nav reveal + active-link tracking
     - Video lightbox (<dialog>)
     - Recently-viewed tracking
     - Share button (native share with clipboard fallback)
   ============================================================ */
(function () {
  'use strict';

  /* --- Gallery thumb swap ---
     Document-delegated click. We resolve the main image on every click
     rather than caching it at init time because the previous version
     would `return early` if the image element wasn't yet in the DOM at
     init — which silently disabled the whole feature on Shopify themes
     where image_tag drops custom `id:` attributes. The fallbacks below
     also let us find the main image even if its id changes. */
  function initGallery() {
    document.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-pg-thumb]');
      if (!thumb) return;
      e.preventDefault();

      /* Resolve the main image on demand. Try the id first (cheap),
         then fall back to the `.pg-main__img` class which is always
         server-rendered, then to any img inside `#pg-main`. */
      var main =
        document.getElementById('pg-main-img') ||
        document.querySelector('.pg-main__img') ||
        document.querySelector('#pg-main img');
      if (!main) {
        if (window.console && console.warn) console.warn('[product.js] thumb click: no main image found');
        return;
      }

      var src = thumb.getAttribute('data-pg-src');
      var alt = thumb.getAttribute('data-pg-alt');
      if (src) {
        main.setAttribute('src', src);
        main.removeAttribute('srcset');
      }
      if (alt) main.setAttribute('alt', alt);
      document.querySelectorAll('[data-pg-thumb]').forEach(function (o) {
        o.classList.remove('pg-thumb--active');
        o.setAttribute('aria-selected', 'false');
      });
      thumb.classList.add('pg-thumb--active');
      thumb.setAttribute('aria-selected', 'true');
    });
  }

  /* --- Phone gallery: swipe, prev / next and "2 / 7" counter ---
     Below 720px the thumbnail row is hidden (CSS), so the main image is
     swiped instead. Every step simply clicks the next or previous
     thumbnail, so the image swap, the active state and the zoom lightbox
     all run through the one existing code path. */
  function initGallerySwipe() {
    var main = document.getElementById('pg-main');
    if (!main) return;
    function thumbs() { return Array.prototype.slice.call(document.querySelectorAll('[data-pg-thumb]')); }
    function current() {
      var t = thumbs();
      for (var i = 0; i < t.length; i++) if (t[i].classList.contains('pg-thumb--active')) return i;
      return 0;
    }
    function show(i) {
      var t = thumbs();
      if (t.length < 2) return;
      t[(i + t.length) % t.length].click();
    }
    var counter = main.querySelector('[data-pg-counter]');
    function syncCounter() {
      if (counter) counter.textContent = (current() + 1) + ' / ' + thumbs().length;
    }

    var x0 = null, y0 = null;
    main.addEventListener('touchstart', function (e) {
      if (e.touches.length !== 1) { x0 = null; return; }
      x0 = e.touches[0].clientX;
      y0 = e.touches[0].clientY;
    }, { passive: true });
    main.addEventListener('touchend', function (e) {
      if (x0 === null) return;
      var dx = e.changedTouches[0].clientX - x0;
      var dy = e.changedTouches[0].clientY - y0;
      x0 = null;
      /* A deliberate sideways swipe only; vertical drags keep scrolling. */
      if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(dy) * 1.5) return;
      show(current() + (dx < 0 ? 1 : -1));
    }, { passive: true });

    main.addEventListener('click', function (e) {
      var step = e.target.closest('[data-pg-step]');
      if (!step) return;
      e.preventDefault();
      show(current() + parseInt(step.getAttribute('data-pg-step'), 10));
    });
    /* Keep the counter right whichever way the image changed. */
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-pg-thumb]')) setTimeout(syncCounter, 0);
    });
    syncCounter();
  }

  /* --- Image lightbox / gallery ---
     Opens a full-viewport modal showing the current gallery image
     with prev/next navigation, keyboard arrows, and click-to-dismiss
     on the backdrop. Image list is built from the [data-pg-thumb]
     elements on the page, so it stays in sync with the gallery
     thumbnails on every product. Created lazily on first open. */
  function initImageZoom() {
    var lightbox = null;
    var images = [];
    var current = 0;

    function buildImages() {
      images = [];
      document.querySelectorAll('[data-pg-thumb]').forEach(function (t) {
        var src = t.getAttribute('data-pg-src');
        if (!src) return;
        /* Upscale Shopify CDN width param for the lightbox view. */
        var hi = src.replace(/(_)\d{2,4}x(\d{0,4})?(\.[a-z0-9]+)/i, '$1' + '2048x$2$3');
        if (hi === src) hi = src.replace(/(\?|&)width=\d+/i, '$1width=2048');
        images.push({
          src: hi,
          alt: t.getAttribute('data-pg-alt') || ''
        });
      });
    }

    function ensureLightbox() {
      if (lightbox) return lightbox;
      lightbox = document.createElement('div');
      lightbox.className = 'pg-lightbox';
      lightbox.setAttribute('role', 'dialog');
      lightbox.setAttribute('aria-modal', 'true');
      lightbox.setAttribute('aria-label', 'Product image gallery');
      lightbox.innerHTML =
        '<div class="pg-lightbox__backdrop" data-pg-lightbox-close></div>' +
        '<button type="button" class="pg-lightbox__nav pg-lightbox__nav--prev" data-pg-lightbox-prev aria-label="Previous image">' +
          '<svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
            '<path d="M10 4l-4 4 4 4"/>' +
          '</svg>' +
        '</button>' +
        '<button type="button" class="pg-lightbox__nav pg-lightbox__nav--next" data-pg-lightbox-next aria-label="Next image">' +
          '<svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
            '<path d="M6 4l4 4-4 4"/>' +
          '</svg>' +
        '</button>' +
        '<div class="pg-lightbox__panel">' +
          '<img class="pg-lightbox__img" alt="">' +
        '</div>' +
        '<button type="button" class="pg-lightbox__close" data-pg-lightbox-close aria-label="Close gallery">' +
          '<svg width="22" height="22" viewBox="0 0 16 16" fill="none" stroke="currentColor" stroke-width="1.8" aria-hidden="true">' +
            '<path d="M3 3l10 10M13 3 3 13"/>' +
          '</svg>' +
        '</button>' +
        '<div class="pg-lightbox__strip" data-pg-lightbox-strip></div>' +
        '<div class="pg-lightbox__counter" data-pg-lightbox-counter></div>';
      document.body.appendChild(lightbox);
      return lightbox;
    }

    function paint() {
      if (!lightbox || !images.length) return;
      var item = images[current];
      var img = lightbox.querySelector('.pg-lightbox__img');
      var counter = lightbox.querySelector('[data-pg-lightbox-counter]');
      var strip = lightbox.querySelector('[data-pg-lightbox-strip]');
      if (img) {
        img.src = item.src;
        img.alt = item.alt;
      }
      if (counter) counter.textContent = (current + 1) + ' / ' + images.length;

      /* Rebuild the thumbnail strip on every paint so it reflects the
         current image set + active state. Each thumb is a button that
         jumps to that index. We downscale via the Shopify CDN width
         param so the strip isn't loading 2048px thumbs. */
      var single = images.length <= 1;
      if (strip) {
        if (single) {
          strip.style.display = 'none';
        } else {
          strip.style.display = '';
          var html = '';
          for (var i = 0; i < images.length; i++) {
            var thumbSrc = images[i].src.replace(/_2048x/, '_200x');
            html += '<button type="button" class="pg-lightbox__strip-thumb' +
              (i === current ? ' pg-lightbox__strip-thumb--active' : '') +
              '" data-pg-lightbox-go="' + i + '" aria-label="Go to image ' + (i + 1) + '">' +
              '<img src="' + thumbSrc + '" alt="" loading="lazy">' +
              '</button>';
          }
          strip.innerHTML = html;
          /* Scroll the active thumb into view */
          var active = strip.querySelector('.pg-lightbox__strip-thumb--active');
          if (active && active.scrollIntoView) {
            active.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
          }
        }
      }

      /* Hide nav buttons + counter if only one image. */
      var prev = lightbox.querySelector('[data-pg-lightbox-prev]');
      var next = lightbox.querySelector('[data-pg-lightbox-next]');
      if (prev) prev.style.display = single ? 'none' : '';
      if (next) next.style.display = single ? 'none' : '';
      if (counter) counter.style.display = single ? 'none' : '';
    }

    function goTo(idx) {
      if (idx < 0 || idx >= images.length) return;
      current = idx;
      paint();
    }

    function openLightbox() {
      buildImages();
      if (!images.length) return;
      /* Default to the currently-displayed main image's index. */
      var main =
        document.getElementById('pg-main-img') ||
        document.querySelector('.pg-main__img') ||
        document.querySelector('#pg-main img');
      var mainSrc = main ? main.getAttribute('src') : '';
      current = 0;
      for (var i = 0; i < images.length; i++) {
        /* Match by URL stem (the Shopify CDN size param differs). */
        var stem = images[i].src.split('?')[0].replace(/_\d{2,4}x\d{0,4}\./, '_.');
        var mainStem = (mainSrc || '').split('?')[0].replace(/_\d{2,4}x\d{0,4}\./, '_.');
        if (stem === mainStem) { current = i; break; }
      }
      ensureLightbox();
      paint();
      lightbox.classList.add('is-open');
      document.body.style.overflow = 'hidden';
    }

    function closeLightbox() {
      if (!lightbox) return;
      lightbox.classList.remove('is-open');
      document.body.style.overflow = '';
    }

    function step(delta) {
      if (!images.length) return;
      current = (current + delta + images.length) % images.length;
      paint();
    }

    /* Document-delegated click handler — opens on zoom button, plus
       handles all the lightbox-internal controls (prev / next / close
       / backdrop). */
    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-pg-lightbox-prev]')) {
        e.preventDefault();
        step(-1);
        return;
      }
      if (e.target.closest('[data-pg-lightbox-next]')) {
        e.preventDefault();
        step(1);
        return;
      }
      if (e.target.closest('[data-pg-lightbox-close]')) {
        e.preventDefault();
        closeLightbox();
        return;
      }
      var goBtn = e.target.closest('[data-pg-lightbox-go]');
      if (goBtn) {
        e.preventDefault();
        goTo(parseInt(goBtn.getAttribute('data-pg-lightbox-go'), 10));
        return;
      }
      if (e.target.closest('[data-pg-zoom]')) {
        e.preventDefault();
        openLightbox();
        return;
      }
      /* Single-image lightbox trigger (e.g. performance curve). Carries
         its image URL + alt in data attributes; we substitute the
         gallery image array with just that one entry before opening. */
      var singleTrigger = e.target.closest('[data-pg-lightbox-single]');
      if (singleTrigger) {
        e.preventDefault();
        var src = singleTrigger.getAttribute('data-pg-lightbox-src');
        if (!src) return;
        images = [{ src: src, alt: singleTrigger.getAttribute('data-pg-lightbox-alt') || '' }];
        current = 0;
        ensureLightbox();
        paint();
        lightbox.classList.add('is-open');
        document.body.style.overflow = 'hidden';
      }
    });

    document.addEventListener('keydown', function (e) {
      if (!lightbox || !lightbox.classList.contains('is-open')) return;
      if (e.key === 'Escape')     closeLightbox();
      if (e.key === 'ArrowLeft')  step(-1);
      if (e.key === 'ArrowRight') step(1);
    });
  }

  /* VAT toggle was removed — all prices now render inc VAT with a
     small "(Exc VAT)" sibling. See product-gallery.liquid for the
     dual-price markup and the variant-change handler at the bottom
     of this file for the cents → display refresh on variant swap. */

  /* --- Quantity stepper --- */
  function initQty() {
    var input = document.querySelector('[data-qty-input]');
    var dec = document.querySelector('[data-qty-dec]');
    var inc = document.querySelector('[data-qty-inc]');
    if (!input) return;
    function set(v) { input.value = Math.max(1, parseInt(v, 10) || 1); }
    if (dec) dec.addEventListener('click', function () { set(parseInt(input.value, 10) - 1); });
    if (inc) inc.addEventListener('click', function () { set(parseInt(input.value, 10) + 1); });
    input.addEventListener('change', function () { set(input.value); });
  }

  /* The dispatch message ([data-delivery-countdown]) is written by
     assets/delivery.js in UK time; setStock() below only shows or hides it. */

  /* --- Sticky sub-nav --- */
  function initStickyNav() {
           var nav = document.querySelector('[data-product-sticky]');
    if (!nav) return;

    /* JS-driven sticky pinning. CSS position:sticky should be doing
       this natively, but quirks in this theme's main/section layout
       prevent it from working on the section wrapper. So instead:
       the bar lives in natural document flow (taking up its 46px of
       space — held open by the section wrapper's min-height); when
       scroll passes the bar's natural top minus the header stack
       offset, we add `.product-sticky--pinned` which swaps the bar
       to position:fixed at top: 134px (or 120px on mobile). Scroll
       back up past that threshold and the class is removed, restoring
       the bar to its natural flow position. */
    var wrapper = nav.closest('[id^="shopify-section-product-sticky-nav"]');
    if (wrapper) {
      function updatePin() {
        var headerOffset = window.innerWidth <= 900 ? 120 : 134;
        var threshold = wrapper.offsetTop - headerOffset;
        nav.classList.toggle('product-sticky--pinned', window.scrollY > threshold);
      }
      window.addEventListener('scroll', updatePin, { passive: true });
      window.addEventListener('resize', updatePin);
      updatePin();
    }
    var links = nav.querySelectorAll('[data-sticky-link]');
    var byId = {};
    links.forEach(function (l) {
      var id = l.getAttribute('href').replace('#', '');
      var target = document.getElementById(id);
      if (target) byId[id] = l;
    });
    var ids = Object.keys(byId);
    if (!ids.length) return;

    var activeObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (e) {
        if (e.isIntersecting) {
          links.forEach(function (l) { l.classList.remove('is-active'); });
          var link = byId[e.target.id];
          if (link) link.classList.add('is-active');
        }
      });
    }, { rootMargin: '-40% 0px -50% 0px', threshold: 0 });

    ids.forEach(function (id) {
      var t = document.getElementById(id);
      if (t) activeObserver.observe(t);
    });
  }

  /* --- Video lightbox --- */
  function initVideoLightbox() {
    var dialog = document.querySelector('[data-video-dialog]');
    if (!dialog) return;
    var frame = dialog.querySelector('[data-video-dialog-frame]');
    var title = dialog.querySelector('[data-video-dialog-title]');
    var closeBtn = dialog.querySelector('[data-video-close]');
    if (!frame) return;

    function youtubeEmbed(url) {
      var m = url.match(/(?:youtu\.be\/|youtube\.com\/(?:watch\?v=|embed\/|shorts\/))([\w-]{11})/);
      return m ? 'https://www.youtube-nocookie.com/embed/' + m[1] + '?autoplay=1' : null;
    }
    function vimeoEmbed(url) {
      var m = url.match(/vimeo\.com\/(\d+)/);
      return m ? 'https://player.vimeo.com/video/' + m[1] + '?autoplay=1' : null;
    }

    document.querySelectorAll('[data-video-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var url = btn.getAttribute('data-video-url');
        var name = btn.getAttribute('data-video-title') || '';
        if (!url) return;
        if (title) title.textContent = name;
        var embed = youtubeEmbed(url) || vimeoEmbed(url);
        if (embed) {
          frame.innerHTML = '<iframe src="' + embed + '" allow="autoplay; encrypted-media; picture-in-picture" allowfullscreen></iframe>';
        } else {
          frame.innerHTML = '<video src="' + url + '" controls autoplay></video>';
        }
        if (typeof dialog.showModal === 'function') dialog.showModal();
        else dialog.setAttribute('open', '');
      });
    });

    function close() {
      frame.innerHTML = '';
      if (typeof dialog.close === 'function') dialog.close();
      else dialog.removeAttribute('open');
    }
    if (closeBtn) closeBtn.addEventListener('click', close);
    dialog.addEventListener('click', function (e) {
      var rect = dialog.getBoundingClientRect();
      var inside = e.clientX >= rect.left && e.clientX <= rect.right && e.clientY >= rect.top && e.clientY <= rect.bottom;
      if (!inside) close();
    });
    dialog.addEventListener('cancel', function () { frame.innerHTML = ''; });
  }

  /* --- Share button --- */
  function initShare() {
    var btn = document.querySelector('[data-share]');
    if (!btn) return;
    btn.addEventListener('click', function () {
      var data = { title: document.title, url: window.location.href };
      if (navigator.share) {
        navigator.share(data).catch(function () { /* user dismissed */ });
      } else if (navigator.clipboard) {
        navigator.clipboard.writeText(window.location.href).then(function () {
          var orig = btn.innerHTML;
          btn.innerHTML = '✓ Link copied';
          setTimeout(function () { btn.innerHTML = orig; }, 1600);
        });
      }
    });
  }

  /* --- Track recently viewed --- */
  function initRecentlyViewed() {
    var section = document.querySelector('[data-product-section]');
    if (!section) return;
    var handle = section.getAttribute('data-product-handle');
    if (!handle) return;
    window.FAWP = window.FAWP || {};
    window.FAWP.currentProductHandle = handle;
    if (window.FAWP.trackRecentlyViewed) {
      window.FAWP.trackRecentlyViewed(handle);
    } else {
      // Defer until recently-viewed.js loads.
      window.addEventListener('load', function () {
        if (window.FAWP && window.FAWP.trackRecentlyViewed) {
          window.FAWP.trackRecentlyViewed(handle);
        }
      });
    }
  }

  /* --- Variant picker ---
     Wires the "Choose Model" chip group. Reads the full variant
     catalogue from the inline [data-variants-json] script, listens for
     chip clicks, recomputes the matching variant by comparing the
     chosen option values across all variants, and updates:
       - hidden form id
       - active chip state + aria-pressed
       - per-option "current value" label
       - displayed price (inc + ex VAT base cents on the price element,
         so the VAT toggle keeps working after a swap)
       - main gallery image (when the variant has a featured_image)
       - Add-to-basket label / disabled state for sold-out variants
  */
  function initVariantPicker() {
    var picker = document.querySelector('[data-variant-picker]');
    if (!picker) return;
    var json = picker.querySelector('[data-variants-json]');
    if (!json) return;

    var variants;
    try { variants = JSON.parse(json.textContent || '[]'); }
    catch (e) { return; }
    if (!variants.length) return;

    /* Build initial selection from active chips. We scope to
       `.bb-variant-group` (the fieldset) rather than
       `[data-option-index]` because chip buttons also carry that
       attribute — scoping by class avoids matching the chips here
       and finding zero descendants. */
    var groups = picker.querySelectorAll('.bb-variant-group');
    var selected = [];
    groups.forEach(function (group) {
      var idx = parseInt(group.getAttribute('data-option-index'), 10);
      var active = group.querySelector('.bb-variant-chip--active');
      if (active) selected[idx] = active.getAttribute('data-option-value');
    });

    var idInput = document.querySelector('[data-variant-id]');
    var priceEl = document.querySelector('[data-price-inc]');
    var priceExEl = document.querySelector('[data-price-ex]');
    var addBtn  = document.querySelector('.bb-add');
    var mpnWrap = document.querySelector('[data-mpn]');
    /* Resolve the main image on demand (same fallback cascade as the
       gallery click handler). Caching at init time silently broke on
       themes where image_tag drops the `id:` attribute, leaving variant
       image swaps doing nothing. */
    function getMainImg() {
      return document.getElementById('pg-main-img') ||
             document.querySelector('.pg-main__img') ||
             document.querySelector('#pg-main img');
    }

    function findVariant(options) {
      for (var i = 0; i < variants.length; i++) {
        var v = variants[i];
        if (!v.options) continue;
        var match = true;
        for (var j = 0; j < options.length; j++) {
          if (v.options[j] !== options[j]) { match = false; break; }
        }
        if (match) return v;
      }
      return null;
    }

    /* In the visitor's currency (snippets/market-vat.liquid sets the format). */
    function formatMoney(cents) { return (window.theme && window.theme.formatMoney) ? window.theme.formatMoney(cents) : '£' + (cents / 100).toFixed(2); }

    /* Delivery line under Add to basket: the same rules as
       snippets/delivery-rates.liquid, from the bands and wording it puts on
       the line (data-fd-rates), for the chosen variant's weight and price. */
    var rateLine = document.querySelector('[data-fd-rates]');
    function setDeliveryLine(v) {
      if (!rateLine || !v) return;
      try {
        var d = JSON.parse(rateLine.getAttribute('data-fd-rates'));
        var w = v.weight || 0, text;
        var free = d.free.on && d.free.threshold > 0 && v.price >= d.free.threshold && (d.free.maxGrams <= 0 || w <= d.free.maxGrams);
        if (w > d.quoteOver) text = d.text.quote;
        else if (free) text = d.text.free;
        else {
          var pence = d.bands[0][1];
          if (w > 0) {
            for (var i = 0; i < d.bands.length; i++) { if (w <= d.bands[i][0]) { pence = d.bands[i][1]; break; } }
          }
          text = d.text.from.replace('{price}', formatMoney(pence).replace(/[.,]00(?!\d)/, ''));
        }
        var span = rateLine.querySelector('span');
        if (span) span.textContent = text;
      } catch (e) { /* keep the line as rendered */ }
    }

    /* Stock line + delivery countdown follow the chosen variant, not the
       product as a whole. `low` in the variants JSON is the quantity when
       1-4 are left (null otherwise). */
    var stockRow = document.querySelector('[data-stock-row]');
    var stockText = stockRow ? stockRow.querySelector('.bb-stock-text') : null;
    var countdown = document.querySelector('[data-delivery-countdown]');
    var anyAvailable = variants.some(function (x) { return x.available; });
    /* Products with a lead time (custom.lead_time_text / _days) carry it
       on the stock row, and the stock line shows only that text, with no
       "In stock" prefix; the countdown isn't rendered for them at all. */
    var leadTime = stockRow ? stockRow.getAttribute('data-lead-time') : '';
    function setStock(v) {
      /* Dispatch message only for a variant that is in stock now ("stocked":
         not tracked, or quantity above 0), never a backorder. */
      if (countdown) countdown.hidden = !(v && v.available && v.stocked !== false);
      if (!stockRow || !stockText) return;
      if (!v) { stockRow.hidden = true; return; }
      stockRow.hidden = false;
      stockRow.classList.remove('bb-stock-row--out', 'bb-stock-row--low');
      if (!v.available) {
        stockRow.classList.add('bb-stock-row--out');
        stockText.textContent = (anyAvailable ? 'This option is out of stock.' : 'Out of stock.') + ' Call 0115 987 0358 for availability';
      } else if (leadTime) {
        stockText.textContent = leadTime;
      } else if (v.low) {
        stockRow.classList.add('bb-stock-row--low');
        stockText.innerHTML = 'Only <strong>' + v.low + '</strong> left, order soon';
      } else {
        stockText.textContent = 'In stock · Ready to Ship';
      }
    }

    /* Grey out option values that don't exist alongside the current
       selection (e.g. "Add Buna Seal Set" only on diesel models). They
       stay clickable so the customer can change the other option first,
       and a note under the group says why. Works for any product. */
    function comboExists(idx, value) {
      for (var i = 0; i < variants.length; i++) {
        var o = variants[i].options;
        if (!o || o[idx] !== value) continue;
        var ok = true;
        for (var j = 0; j < selected.length; j++) {
          if (j === idx || selected[j] == null) continue;
          if (o[j] !== selected[j]) { ok = false; break; }
        }
        if (ok) return true;
      }
      return false;
    }
    function refreshChips() {
      groups.forEach(function (group) {
        var idx = parseInt(group.getAttribute('data-option-index'), 10);
        var others = [];
        for (var j = 0; j < selected.length; j++) {
          if (j !== idx && selected[j] != null) others.push(selected[j]);
        }
        var anyNa = false;
        group.querySelectorAll('[data-variant-option-value]').forEach(function (chip) {
          var ok = comboExists(idx, chip.getAttribute('data-option-value'));
          chip.classList.toggle('bb-variant-chip--na', !ok);
          if (ok) {
            chip.removeAttribute('aria-disabled');
            chip.removeAttribute('title');
          } else {
            anyNa = true;
            chip.setAttribute('aria-disabled', 'true');
            chip.setAttribute('title', 'Not available with ' + others.join(' / '));
          }
        });
        var note = group.querySelector('.bb-variant-note');
        if (anyNa) {
          if (!note) {
            note = document.createElement('p');
            note.className = 'bb-variant-note';
            group.appendChild(note);
          }
          note.textContent = 'Greyed-out options aren’t available with ' + others.join(' / ') + '.';
          note.hidden = false;
        } else if (note) {
          note.hidden = true;
        }
      });
    }

    function applyVariant(v) {
      setStock(v);
      setDeliveryLine(v);
      if (!v) {
        /* Impossible option combination (no matching variant): never leave
           the previous variant in the form or a live Add button, or the
           customer buys the wrong thing (e.g. Buna Seal Set selected on a
           petrol model). Blank the id and disable the button until they
           change a selection back to a real variant. */
        if (idInput) idInput.value = '';
        if (addBtn) {
          addBtn.setAttribute('disabled', '');
          addBtn.firstChild && (addBtn.firstChild.nodeValue = 'Unavailable in this combination ');
        }
        return;
      }
      if (idInput) idInput.value = v.id;
            if (priceEl) {
        /* `v.price` from product.variants JSON is the stored ex-VAT value
           in cents (Shopify Tax has "All prices include tax" OFF). Multiply
           by 1.2 for the inc-VAT headline — the figure customers pay at
           checkout — matching the initial Liquid render in this same buy
           box, product cards, cart, etc. */
        var priceInc = Math.round(v.price * ((window.theme && window.theme.market && window.theme.market.vatRate) || 1.2));
        priceEl.setAttribute('data-price-cents', priceInc);
        priceEl.textContent = formatMoney(priceInc);
      }
      if (priceExEl) {
        /* Companion ex-VAT figure beside the inc-VAT primary — the raw
           stored value (no multiplication). UK 20% VAT. */
        priceExEl.textContent = formatMoney(v.price) + ' (Exc VAT)';
      }
      if (addBtn) {
        if (v.available) {
          addBtn.removeAttribute('disabled');
          addBtn.firstChild && (addBtn.firstChild.nodeValue = 'Add to basket ');
        } else {
          addBtn.setAttribute('disabled', '');
          addBtn.firstChild && (addBtn.firstChild.nodeValue = 'Out of stock ');
        }
      }
      var mainImg = getMainImg();
      if (mainImg && v.image && v.image.src) {
        mainImg.src = v.image.src;
        if (v.image.alt) mainImg.alt = v.image.alt;
        /* Drop srcset so the new src isn't overridden by the
           responsive set baked in from the original featured image. */
        if (mainImg.hasAttribute('srcset')) mainImg.removeAttribute('srcset');
      }
      /* MPN line: show + swap for variants that carry one, hide otherwise. */
      if (mpnWrap) {
        if (v.mpn) {
          var mpnVal = mpnWrap.querySelector('[data-mpn-value]');
          if (mpnVal) mpnVal.textContent = v.mpn;
          mpnWrap.hidden = false;
        } else {
          mpnWrap.hidden = true;
        }
      }
    }

    /* Initial sync — applies whatever variant matches the current
       selection (in case the URL had ?variant= or selected_or_first
       chose something other than the very first). */
    applyVariant(findVariant(selected));
    refreshChips();

    picker.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-variant-option-value]');
      if (!chip) return;
      e.preventDefault();
      var idx = parseInt(chip.getAttribute('data-option-index'), 10);
      var value = chip.getAttribute('data-option-value');
      selected[idx] = value;

      /* Toggle active state within this option group only.
         IMPORTANT: use `.bb-variant-group` here, not
         `[data-option-index]` — both the fieldset and the chip
         carry `data-option-index`, so `closest('[data-option-index]')`
         would return the chip itself and the querySelectorAll below
         would find nothing, leaving the previously-active chip stuck
         in its orange state. */
      var group = chip.closest('.bb-variant-group');
      if (group) {
        group.querySelectorAll('[data-variant-option-value]').forEach(function (c) {
          var on = c === chip;
          c.classList.toggle('bb-variant-chip--active', on);
          c.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
      var currentLabel = picker.querySelector('[data-option-current="' + idx + '"]');
      if (currentLabel) currentLabel.textContent = value;

      var variant = findVariant(selected);
      applyVariant(variant);
      updateVariantUrl(variant);
      refreshChips();
    });
  }

  /* Sync the `?variant=ID` query param with the chosen variant so:
       - copy/paste of the URL takes someone to the same variant,
       - browser back/forward navigation preserves selection,
       - Shopify's analytics + product recommendations key off the URL.
     We use replaceState (not pushState) so each chip click doesn't
     spam the browser history — the user can still back-button out of
     the product page in one step. Mirrors how Empire and Dawn handle
     this in their own variant pickers. */
  function updateVariantUrl(variant) {
    if (!variant || !variant.id) return;
    if (typeof window.history === 'undefined' || !window.history.replaceState) return;
    try {
      var url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url.toString());
    } catch (e) { /* IE11 / old browsers — ignore */ }
  }

  function init() {
    initGallery();
    initGallerySwipe();
    initImageZoom();
    initVariantPicker();
    initQty();
    initStickyNav();
    initVideoLightbox();
    initShare();
    initRecentlyViewed();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
